// /app/frontend/src/pages/seo/SafePolyDatingPage.jsx
// Focus: TACTICAL safety checklist for dating in poly relationships.
import React from 'react';
import PublicSEOHeader from '../../components/PublicSEOHeader';
import {
  SEOHead,
  organizationSchema,
  buildBreadcrumbSchema,
  GradientHero,
  FeatureCard,
  RelatedGuides,
  BottomCTA,
} from './SEOUtils';

export default function SafePolyDatingPage() {
  const path = '/safe-poly-dating';
  const schemas = [
    organizationSchema,
    buildBreadcrumbSchema([
      { name: 'Home', url: '/' },
      { name: 'Guides', url: '/guides' },
      { name: 'Safe Poly Dating Guide', url: path },
    ]),
  ];

  return (
    <div className="min-h-screen bg-white">
      <PublicSEOHeader />
      <SEOHead
        title="Safe Poly Dating Guide"
        description="Discover practical tips for building healthy, respectful, and safe polyamorous relationships."
        canonical={path}
        schemas={schemas}
      />

      <GradientHero
        eyebrow="A UnityLove Guide"
        headline="Building Safe Poly Relationships"
        sub="Polyamory does not require risk-taking — it requires intentional safety practices. The tactical checklist most experienced poly people wish they had had on day one."
        primaryCta={{ label: 'Create Your Profile', to: '/auth?mode=signup' }}
        secondaryCta={{ label: 'Ethical Polyamory 101', to: '/ethical-polyamory-for-beginners' }}
      />

      <main className="max-w-4xl mx-auto px-4 py-14">
        {/* Intro */}
        <section className="mb-12 max-w-2xl mx-auto text-center">
          <p className="text-lg text-slate-700 leading-relaxed">
            Safety in poly is a practice, not a personality trait. Even the most experienced poly people benefit from refreshing the basics regularly. Here are the practical, day-to-day habits that protect every adult in the network.
          </p>
        </section>

        {/* Four dimensions of safety */}
        <section className="mb-14">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-6">
            The Four Dimensions Of Poly Safety
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <FeatureCard
              icon="❤️"
              accent="rose"
              title="Emotional Safety"
              body="Every partner can be honest about their feelings — including the hard ones — without being punished, dismissed, or having information weaponized later."
            />
            <FeatureCard
              icon="🩺"
              accent="emerald"
              title="Sexual Health Safety"
              body="Clear protocols for testing, barriers, and disclosure. Agreements are explicit, regularly revisited, and never assumed."
            />
            <FeatureCard
              icon="🔒"
              accent="sky"
              title="Privacy & Information Safety"
              body="Who knows what, and how. Partners do not out each other. Information shared in trust stays in trust."
            />
            <FeatureCard
              icon="🛡️"
              accent="amber"
              title="Physical & Personal Safety"
              body="First meetings are public. Locations are shared with a trusted person. Verification is the norm, not the exception, especially when dating online."
            />
          </div>
        </section>

        {/* First date safety */}
        <section className="mb-14">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-4">
            First-Date Practical Habits
          </h2>
          <ul className="space-y-3">
            {[
              ['Always meet in public the first time.', 'Even if you have video-called for weeks. New environments reveal new information.'],
              ['Share your plans with someone in your network.', 'A partner or close friend should know who you are meeting, where, and when you expect to be home.'],
              ['Trust your gut on profile authenticity.', 'Use verification features. Reverse-image-search profile photos if anything feels off. UnityLove identity verification is here for a reason.'],
              ['Drive yourself or use your own transport.', 'Avoid getting picked up the first time. You want to be able to leave on your own terms.'],
              ['Keep your phone charged and accessible.', 'A pre-arranged check-in text or call is a good practice, especially for longer dates.'],
              ['Pace physical intimacy.', 'The healthiest poly relationships often spend several dates before physical intimacy. There is no rush.'],
            ].map(([title, body]) => (
              <li key={title} className="flex items-start gap-3 rounded-xl border border-slate-200 bg-white p-4">
                <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-rose-100 text-rose-700 text-xs font-bold">
                  ✓
                </span>
                <div>
                  <p className="font-semibold text-slate-900">{title}</p>
                  <p className="text-slate-700 mt-1 text-sm">{body}</p>
                </div>
              </li>
            ))}
          </ul>
        </section>

        {/* Sexual health */}
        <section className="mb-14 bg-slate-50 rounded-3xl p-6 sm:p-8">
          <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
            Sexual Health In Poly
          </h2>
          <div className="space-y-3 text-slate-700 leading-relaxed">
            <p>
              Sexual health agreements are part of the foundation of any healthy poly network. The specifics vary — barriers always, barriers for new partners only, fluid-bonding within a closed group, regular STI testing on a defined schedule — but the principles are universal:
            </p>
            <ul className="list-disc list-inside text-slate-700 ml-2 space-y-1">
              <li>Talk about it before it is needed, not after.</li>
              <li>Disclose status changes proactively. Always.</li>
              <li>Use the same standards for every partner, not stricter ones for some and looser for others.</li>
              <li>Revisit the protocol when new partners enter the network.</li>
            </ul>
            <p>
              No protocol is bulletproof, but a clear shared one is dramatically safer than vague assumptions.
            </p>
          </div>
        </section>

        {/* Emotional safety practices */}
        <section className="mb-14">
          <h2 className="font-playfair text-3xl font-bold text-slate-900 mb-4">
            Emotional Safety Practices
          </h2>
          <div className="space-y-3 text-slate-700 leading-relaxed">
            <p>
              Most poly relationships do not fail from sexual or physical safety failures. They fail from emotional ones — slowly, over months of small slights that go unnamed. Build these habits early:
            </p>
            <ul className="list-disc list-inside ml-2 space-y-1">
              <li>Schedule regular relationship check-ins, not just when something is wrong.</li>
              <li>Name feelings before they become resentments. Use specific words.</li>
              <li>Do not triangulate — bring concerns directly to the person involved.</li>
              <li>Apologize specifically when wrong. Vague apologies do not repair trust.</li>
              <li>Make space for individual time, hobbies, and friendships outside the network.</li>
              <li>Notice when one partner has become a sounding-board for complaints about another. That dynamic is unsustainable.</li>
            </ul>
          </div>
        </section>

        {/* Red flags */}
        <section className="mb-14 rounded-2xl border border-rose-200 bg-rose-50 p-6">
          <h2 className="font-playfair text-2xl sm:text-3xl font-bold text-rose-900 mb-4">
            Red Flags When Dating
          </h2>
          <ul className="space-y-2 text-rose-900 list-disc list-inside">
            <li>Refuses to share information about other partners</li>
            <li>Pressures you to move faster than feels right</li>
            <li>Speaks badly about existing partners early in the relationship</li>
            <li>Has agreements with their other partners you would not consent to if they were yours</li>
            <li>Pushes back against verification, public meetings, or pacing</li>
            <li>Treats your boundaries as obstacles to negotiate, not facts to honor</li>
          </ul>
          <p className="mt-4 text-rose-900 text-sm font-medium">
            One red flag is information. Two is a pattern. Three is a decision you can make sooner than later.
          </p>
        </section>

        {/* Internal links */}
        <section className="mb-4">
          <h2 className="font-playfair text-2xl font-bold text-slate-900 mb-4">
            Read Next
          </h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <a href="/ethical-polyamory-for-beginners" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Ethical Polyamory For Beginners</p>
              <p className="text-sm text-slate-600 mt-1">The foundations of consent and care.</p>
            </a>
            <a href="/poly-dating-for-couples" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Poly Dating For Couples</p>
              <p className="text-sm text-slate-600 mt-1">For couples exploring ENM together.</p>
            </a>
            <a href="/poly-family-planning" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">Poly Family Planning</p>
              <p className="text-sm text-slate-600 mt-1">Practical planning for plural families.</p>
            </a>
            <a href="/how-to-find-a-sister-wife" className="block rounded-xl border border-slate-200 bg-white p-4 hover:border-rose-400 transition">
              <p className="font-semibold text-slate-900">How To Find A Sister Wife</p>
              <p className="text-sm text-slate-600 mt-1">Building a plural family with intention.</p>
            </a>
          </div>
        </section>
      </main>

      <RelatedGuides currentPath={path} />
      <BottomCTA />
    </div>
  );
}
